import { inject, injectable } from 'inversify'
import { makeObservable, observable, runInAction } from 'mobx'
import { HttpGateway } from '@core/services/http.gateway'
import { IdGeneratorGateway } from '@core/services/id-generator.gateway'
import { ServiceWorkerGateway } from '@core/services/service-worker.gateway'
import { APP_VERSION } from '@core/version'
import { Types } from '@core/ioc/types'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import { MessagePacking, type ActionFailurePm, type ActionResponsePm } from '@core/feedback/feedback.packing'
import { mapReadFailure } from '@core/services/gateway-failure.helpers'
import { z } from '@core/validation/validation.schema'
import { OperationRepository } from '@core/operations/operation.repository'
import type {
  OperationHandle,
  OperationOwner,
  OperationResult,
  ReadonlyOperationState,
} from '@core/operations/operation.types'
import {
  defineOperation,
  operationFailed,
  operationGroup,
  operationId,
  operationKey,
  operationSucceeded,
} from '@core/operations/operation.helpers'

type LoadFailure = ActionFailurePm & Readonly<{ retryable: boolean }>

/**
 * Why a subscribe attempt stopped — richer than a generic ActionFailurePm
 * because the presenter shows a specific line for each ('turn it on in
 * browser settings' reads nothing like 'try again'). The Repository names the
 * reason; the presenter owns the copy (CI-4-ish: no i18n here).
 */
export type PushSubscribeReason = 'unsupported' | 'blocked' | 'server-unavailable' | 'failed'
type PushWriteFailure = Readonly<{ reason: PushSubscribeReason }>

/**
 * This browser's Web Push state (PRODUCT.md "Reminders where the user already
 * looks", Phase P). Everything here is *this device's* — a second phone has
 * its own subscription — so it is user-scoped (forgotten at logout) but NOT
 * an AppLifecycle participant: the app never registers or prompts on boot,
 * only when the user opens Settings and asks. The channel *preference*
 * (`reminderPreferences.channels.push`) lives on ReminderPreferencesRepository;
 * the reminder sweep needs both a live subscription here and that flag on.
 */
export type PushStatusPm = {
  readonly isSupported: boolean
  /** 'default' | 'granted' | 'denied' — 'denied' is only undoable in browser settings. */
  readonly permission: NotificationPermission
  /** This browser currently holds a push subscription registered with the server. */
  readonly isSubscribed: boolean
  /** The server has a VAPID keypair configured. false ⇒ push is unavailable no matter what the browser can do. */
  readonly serverConfigured: boolean
}

const PushPublicKeyDtoSchema = z.object({ publicKey: z.string().nullable() })
const SubscribeResultDtoSchema = z.object({ subscriptionId: z.string() })

const LOAD_STATUS_OPERATION = defineOperation<null, LoadFailure>({
  id: operationId('push.load-status'),
  key: () => operationKey('push-status'),
  groups: () => [operationGroup('session'), operationGroup('push')],
  pending: { kind: 'join' },
  safety: { kind: 'read' },
  timeout: {
    kind: 'after',
    milliseconds: 15_000,
    toFailure: () => ({ code: 'temporarily-unavailable', retryable: false }),
  },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

type WriteArgs = Readonly<{ idempotencyKey: string }>
const WRITE_SENTINEL: WriteArgs = Object.freeze({ idempotencyKey: '' })

const SUBSCRIBE_OPERATION = defineOperation<WriteArgs, PushWriteFailure>({
  id: operationId('push.subscribe'),
  key: () => operationKey('command'),
  groups: () => [operationGroup('session'), operationGroup('push')],
  pending: { kind: 'ignore' },
  safety: { kind: 'idempotency-keyed-write', idempotencyKey: (args) => args.idempotencyKey },
  timeout: { kind: 'after', milliseconds: 15_000, toFailure: () => ({ reason: 'failed' }) },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

const UNSUBSCRIBE_OPERATION = defineOperation<WriteArgs, ActionFailurePm>({
  id: operationId('push.unsubscribe'),
  key: () => operationKey('command'),
  groups: () => [operationGroup('session'), operationGroup('push')],
  pending: { kind: 'ignore' },
  safety: { kind: 'idempotency-keyed-write', idempotencyKey: (args) => args.idempotencyKey },
  timeout: { kind: 'after', milliseconds: 15_000, toFailure: () => ({ code: 'temporarily-unavailable' }) },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

export type PushSubscribeOutcome =
  Readonly<{ success: true }> | Readonly<{ success: false; reason: PushSubscribeReason }>

@injectable()
export class PushRepository implements UserScopedState {
  /** null until loadStatus() runs once. */
  statusPm: PushStatusPm | null = null

  private publicKey: string | null = null
  /** This browser's current push endpoint — the key the server's DELETE takes after a page reload. */
  private endpoint: string | null = null

  private readonly dataGateway: HttpGateway
  private readonly serviceWorker: ServiceWorkerGateway
  private readonly idGenerator: IdGeneratorGateway
  private readonly operationOwner: OperationOwner
  private readonly loadStatusOperation: OperationHandle<null, LoadFailure>
  private readonly subscribeOperation: OperationHandle<WriteArgs, PushWriteFailure>
  private readonly unsubscribeOperation: OperationHandle<WriteArgs, ActionFailurePm>

  constructor(
    @inject(Types.IDataGateway) dataGateway: HttpGateway,
    @inject(Types.IServiceWorkerGateway) serviceWorker: ServiceWorkerGateway,
    @inject(OperationRepository) operationRepository: OperationRepository,
    @inject(IdGeneratorGateway) idGenerator: IdGeneratorGateway,
  ) {
    this.dataGateway = dataGateway
    this.serviceWorker = serviceWorker
    this.idGenerator = idGenerator
    this.operationOwner = operationRepository.createOwner({ id: 'push.repository', lifetime: 'session' })
    this.loadStatusOperation = this.operationOwner.register(LOAD_STATUS_OPERATION)
    this.subscribeOperation = this.operationOwner.register(SUBSCRIBE_OPERATION)
    this.unsubscribeOperation = this.operationOwner.register(UNSUBSCRIBE_OPERATION)
    makeObservable(this, { statusPm: observable.ref })
  }

  get isLoaded(): boolean {
    return this.statusPm !== null
  }

  get loadStatusState(): ReadonlyOperationState<LoadFailure> {
    return this.loadStatusOperation.stateFor(null)
  }

  get subscribeState(): ReadonlyOperationState<PushWriteFailure> {
    return this.subscribeOperation.stateFor(WRITE_SENTINEL)
  }

  get unsubscribeState(): ReadonlyOperationState<ActionFailurePm> {
    return this.unsubscribeOperation.stateFor(WRITE_SENTINEL)
  }

  private buildStatus(overrides: Partial<PushStatusPm> = {}): PushStatusPm {
    const base: PushStatusPm =
      this.statusPm ??
      Object.freeze({
        isSupported: this.serviceWorker.isSupported(),
        permission: this.serviceWorker.permission(),
        isSubscribed: false,
        serverConfigured: this.publicKey !== null,
      })
    return Object.freeze({ ...base, ...overrides })
  }

  loadStatus = async (): Promise<ActionResponsePm> => {
    const result = await this.loadStatusOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.get('/me/push-public-key', { signal, idempotencyKey: null })
        if (!response.success) return operationFailed(mapReadFailure(response))

        const parsed = PushPublicKeyDtoSchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data', retryable: false })

        // Browser reads are local and cannot fail the operation — a missing
        // service worker just means isSupported: false.
        const isSupported = this.serviceWorker.isSupported()
        const current = isSupported ? await this.serviceWorker.currentSubscription() : null
        return operationSucceeded(
          Object.freeze({
            publicKey: parsed.data.publicKey,
            isSupported,
            permission: this.serviceWorker.permission(),
            endpoint: current?.endpoint ?? null,
          }),
        )
      },
      {
        succeeded: (loaded) => {
          this.publicKey = loaded.publicKey
          this.endpoint = loaded.endpoint
          this.statusPm = Object.freeze({
            isSupported: loaded.isSupported,
            permission: loaded.permission,
            isSubscribed: loaded.endpoint !== null,
            serverConfigured: loaded.publicKey !== null,
          })
        },
        failed: null,
      },
    )
    return this.toActionResponsePm(result)
  }

  /**
   * Subscribe this browser and register it with the server. The one place
   * the browser permission prompt is allowed to fire (Settings, user-
   * initiated — every other entry point browsers penalise). Refuses early,
   * without a server call, when the browser can't or the server has no keys.
   */
  subscribe = async (): Promise<PushSubscribeOutcome> => {
    if (!this.serviceWorker.isSupported()) return { success: false, reason: 'unsupported' }
    if (this.publicKey === null) return { success: false, reason: 'server-unavailable' }

    const result = await this.subscribeOperation.run(
      { idempotencyKey: this.idGenerator.generate() },
      async ({ idempotencyKey, signal }) => {
        await this.serviceWorker.ensureRegistered(APP_VERSION)
        const permission = await this.serviceWorker.requestPermission()
        if (permission !== 'granted') return operationFailed({ reason: 'blocked' })

        const subscription = await this.serviceWorker.subscribe(this.publicKey!)
        if (subscription === null) return operationFailed({ reason: 'unsupported' })

        const response = await this.dataGateway.post(
          '/me/push-subscriptions',
          {
            endpoint: subscription.endpoint,
            keys: { p256dh: subscription.keys.p256dh, auth: subscription.keys.auth },
          },
          { signal, idempotencyKey },
        )
        if (!response.success) return operationFailed({ reason: 'failed' })

        const parsed = SubscribeResultDtoSchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ reason: 'failed' })
        return operationSucceeded(subscription.endpoint)
      },
      {
        succeeded: (endpoint) => {
          this.endpoint = endpoint
          this.statusPm = this.buildStatus({ permission: this.serviceWorker.permission(), isSubscribed: true })
        },
        failed: (failure) => {
          // A denied prompt is still a state change worth reflecting.
          if (failure.reason === 'blocked') {
            this.statusPm = this.buildStatus({ permission: this.serviceWorker.permission() })
          }
        },
      },
    )
    if (result.kind === 'succeeded') return { success: true }
    return { success: false, reason: result.kind === 'failed' ? result.error.reason : 'failed' }
  }

  /** Unsubscribe this browser: unregister the push subscription locally, then drop the server row by endpoint. The local half is what stops delivery; the server half stops the sweep retrying a dead endpoint. */
  unsubscribe = async (): Promise<ActionResponsePm> => {
    const endpoint = this.endpoint
    const result = await this.unsubscribeOperation.run(
      { idempotencyKey: this.idGenerator.generate() },
      async ({ signal, idempotencyKey }) => {
        await this.serviceWorker.unsubscribe()
        if (endpoint !== null) {
          const response = await this.dataGateway.delete(
            `/me/push-subscriptions?endpoint=${encodeURIComponent(endpoint)}`,
            { signal, idempotencyKey },
          )
          // A 404 means the row was already gone (the sweep's 410 cleanup
          // beat us) — the local unsubscribe above is what matters, so this
          // is not a failure worth surfacing.
          const alreadyGone =
            !response.success && response.failure.kind === 'rejected' && response.failure.status === 404
          if (!response.success && !alreadyGone) {
            const pm = MessagePacking.unpackGatewayResponseToPm(response)
            return operationFailed(pm.success ? { code: 'temporarily-unavailable' } : pm.failure)
          }
        }
        return operationSucceeded(null)
      },
      {
        succeeded: () => {
          this.endpoint = null
          this.statusPm = this.buildStatus({ isSubscribed: false })
        },
        failed: null,
      },
    )
    return this.toActionResponsePm(result)
  }

  resetUserScopedState = (): void => {
    this.operationOwner.resetAll({ kind: 'session-reset' })
    runInAction(() => {
      this.statusPm = null
      this.publicKey = null
      this.endpoint = null
    })
  }

  private toActionResponsePm(result: OperationResult<unknown, ActionFailurePm>): ActionResponsePm {
    switch (result.kind) {
      case 'succeeded':
        return MessagePacking.succeeded()
      case 'failed':
        return { success: false, failure: result.error }
      case 'cancelled':
      case 'skipped':
        return MessagePacking.cancelled()
    }
  }
}
